import './App.css';
import { useState,  useEffect, useCallback, useRef } from 'react';
import Cookies from "js-cookie";

const Modal = ({ isOpen, onClose, message }) => {
  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="ModalOverlay" onClick={handleOverlayClick}>
      <div className="ModalContent">
        <button className="ModalCloseButton" onClick={onClose}>×</button>
        <p className="ModalMessage">{message}</p>
      </div>
    </div>
  );
};

const Loader = () => {
  return (
    <div className="LoaderOverlay">
      <div className="LoaderSpinner"></div>
    </div>
  );
};


function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [selectedToken, setSelectedToken] = useState(null);
  const [selectedTokenAmount, setSelectedTokenAmount] = useState(0);
  const [recipientAddress, setRecipientAddress] = useState('');
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [selectedNetwork, setSelectedNetwork] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordRegister, setShowPasswordRegister] = useState(false);
  const [showPasswordPrivateKey, setShowPasswordPrivateKey] = useState(false);
  const [paymentProcessFinished, setPaymentProcessFinished] = useState(false);
  const [paymentId, setPaymentId] = useState(null);

  const [networks, setNetworks] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [tokens, setTokens] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  const [isModalPrivateKeyOpen, setIsModalPrivateKeyOpen] = useState(false);
  const [modalPrivateKeyMessage, setModalPrivateKeyMessage] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [isMainPayment, setIsMainPayment] = useState(false);
  const [titlePackage, setTitlePackage] = useState('');

  const [showWalletConfig, setShowWalletConfig] = useState(false);
  const [showWalletConfigPrivateKey, setShowWalletConfigPrivateKey] = useState(false);
  const [showWalletConfigUserProfile, setShowWalletConfigUserProfile] = useState(false);

  const [privateKey, setPrivateKey] = useState('');
  const [showPrivateKey, setShowPrivateKey] = useState(false);

  const [isForgetPassword, setIsForgetPassword] = useState(false);

  const [showTokenDropdown, setShowTokenDropdown] = useState(false);
  const [userProfile, setUserProfile] = useState(null);

  const [preTransaction, setPreTransaction] = useState(null);

  // const urlParams = new URLSearchParams(window.location.search);
  // const hiddenLogout = urlParams.get('hiddenLogout');

  const backend_url = process.env.REACT_APP_PUBLIC_BACKEND_API || 'http://localhost:8000'
  const PIESOCKET_WSS_URL = process.env.REACT_APP_PIESOCKET_WSS_URL || "wss://s14641.blr1.piesocket.com"
  const PIESOCKET_WSS_KEY = process.env.REACT_APP_PIESOCKET_WSS_KEY || "pasvZiXvkZIcIboquQgvIqK8Z4Nu5YL92Xl3OZ43"

  const selectedWalletRef = useRef(null);
  const latestTokensFetchRef = useRef({ requestId: 0, abortController: null });

  useEffect(() => {
    selectedWalletRef.current = selectedWallet;
  }, [selectedWallet]);

  const resetSendFlow = useCallback(() => {
    setSelectedToken(null);
    setSelectedTokenAmount(0);
    setRecipientAddress('');
    setShowTransactionDetails(false);
    setShowConfirmation(false);
    setShowTokenDropdown(false);
    setPreTransaction(null);
  }, []);

  const tokens_fetch = useCallback((walletAddress) => {
    const requestId = latestTokensFetchRef.current.requestId + 1;
    latestTokensFetchRef.current.requestId = requestId;

    if (latestTokensFetchRef.current.abortController) {
      latestTokensFetchRef.current.abortController.abort();
    }

    const abortController = new AbortController();
    latestTokensFetchRef.current.abortController = abortController;

    setIsLoading(true);

    return fetch(`${backend_url}/api/tokens/${walletAddress}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      signal: abortController.signal,
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      if (data.result && data.tokens) {
        return data.tokens;
      }
      return [];
    })
    .catch(error => {
      if (error?.name === 'AbortError') {
        return null;
      }
      console.error('Error fetching tokens:', error);
      return [];
    })
    .finally(() => {
      if (latestTokensFetchRef.current.requestId === requestId) {
        setIsLoading(false);
      }
    });
  }, [backend_url]);

  const networks_fetch = useCallback(() => {
    return fetch(`${backend_url}/api/networks`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })
    .then(response => response.json())
    .then(data => {
      if (data.result && data.networks) {
        return data.networks;
      }
      return [];
    });
  }, [backend_url]);

  const fetchWallets = useCallback((networkId) => {
    return fetch(`${backend_url}/api/wallets/${networkId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })
    .then(response => response.json())
    .then(data => {
      if (data.result && data.wallets) {
        setWallets(data.wallets);
        const firstWallet = data.wallets[0];
        setSelectedWallet(firstWallet);
        resetSendFlow();
        tokens_fetch(firstWallet.address).then(nextTokens => {
          if (nextTokens && selectedWalletRef.current?.address === firstWallet.address) {
            setTokens(nextTokens);
          }
        });
      } else {
        setWallets([]);
        setSelectedWallet(null);
        setTokens([]);
        resetSendFlow();
      }
    });
  }, [backend_url, tokens_fetch, resetSendFlow, setWallets, setSelectedWallet, setTokens]);

  const handleLogin = (e) => {
    e.preventDefault();
    setIsLoading(true);

    const emailInput = e.target.elements.email;
    const passwordInput = e.target.elements.password;

    if (!emailInput || !passwordInput) {
      console.error('Form inputs not found');
      setIsLoading(false);
      return;
    }

    const email = emailInput.value;
    const password = passwordInput.value;

    fetch(`${backend_url}/api/signin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    })
    .then(response => response.json())
    .then(data => {
      if (data.result) {
        Cookies.set('csrftoken', data['X-CSRFToken'], {
          expires: 7,
          secure: process.env.NODE_ENV === 'production',
          sameSite: 'None',
          domain: '.solucionescrypto.com',
          path: '/'
        });
        return networks_fetch();
      } else {
        setModalMessage("Error al iniciar sesión. Por favor, verifica tus credenciales.");
        setIsModalOpen(true);
        throw new Error('Login failed');
      }
    })
    .then(networks => {
      setNetworks(networks);
      const selectedNetworkId = Cookies.get('selected_network');
      if (selectedNetworkId) {
        const selectedNetwork = networks.find(network => network.id === parseInt(selectedNetworkId));
        if (selectedNetwork) {
          setSelectedNetwork(selectedNetwork);
          fetchWallets(selectedNetwork.id);
        }
      }else if (networks.length > 0) {  
        setSelectedNetwork(networks[0]);
        fetchWallets(networks[0].id);
      }
      setIsLoggedIn(true);
    })
    .catch(error => {
      console.error('Login error:', error);
    })
    .finally(() => {
      
    });
  };

  const sendEvent = (paymentId) => {
    const WSS_URL = `${PIESOCKET_WSS_URL}/v3/txs?api_key=${PIESOCKET_WSS_KEY}`
    const ws = new WebSocket(WSS_URL)

    // Connection opened
    ws.addEventListener("open", event => {
      const json_message = JSON.stringify({
          'payment_id': paymentId,
          'status': "PAID"
      })
      ws.send(json_message)
      ws.close()
    });

      // Listen for messages
    ws.addEventListener("message", event => {
      console.log("Message from server ", event.data)
    });
  }

  useEffect(() => {

    const checkSession = async () => {
      const token = Cookies.get('csrftoken');
      // console.log(token)
      console.log("cheking session")

      let config = {
        headers: {
          "X-CSRFToken": token,
        },
        method: "POST",
        credentials: 'include'
      };

      fetch(`${backend_url}/api/healthcheck`, config)
      .then(response => {
        if (!response.ok) {
          setIsLoggedIn(false);
          setSelectedToken(null);
          setRecipientAddress('');
          setShowTransactionDetails(false);
          setShowConfirmation(false);
          setShowCreateAccount(false);
          setNetworks([]);
          setSelectedNetwork(null);
          setWallets([]);
          setSelectedWallet(null);
          setTokens([]);
          setShowWalletConfig(false);
          setPrivateKey('');
          setShowPrivateKey(false);
          setIsForgetPassword(false);
          setShowWalletConfigPrivateKey(false);
          setShowTokenDropdown(false);
  
          setIsModalOpen(false);
          setModalMessage('');
          setModalPrivateKeyMessage('');
          setIsModalPrivateKeyOpen(false);
          setIsLoading(false);
          // setIsMainPayment(false);
          setSelectedTokenAmount(0);
          setShowPassword(false);
          setShowPasswordRegister(false);
          setShowPasswordPrivateKey(false);
          setUserProfile(null);
          setShowWalletConfigUserProfile(false);

          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        console.log(data);
        if (data.result){
          setIsLoggedIn(true);
          return networks_fetch();
        }
      })
      .then(networks => {
        setNetworks(networks);
        const selectedNetworkId = Cookies.get('selected_network');
        if (selectedNetworkId) {
          const selectedNetwork = networks.find(network => network.id === parseInt(selectedNetworkId));
          if (selectedNetwork) {
            setSelectedNetwork(selectedNetwork);
            return fetchWallets(selectedNetwork.id);  
          }
        }else if (networks.length > 0) {
          setSelectedNetwork(networks[0]);
          return fetchWallets(networks[0].id);
        }
      })
      .catch(error => {
        console.error('Login error:', error);
      });
    } 
    checkSession();
  }, [fetchWallets, backend_url, networks_fetch]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const payment = queryParams.get('payment');
    if (payment && tokens && selectedWallet && !paymentProcessFinished) {
      fetch(`${backend_url}/api/transaction/${selectedWallet.address}/${payment}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      .then(response => response.json())
      .then(data => {
        if (data.result) {
          setSelectedToken(data.response.token);
          setSelectedTokenAmount(data.response.package.price);
          setShowTransactionDetails(true);
          setRecipientAddress(data.response.main_wallet_address);
          setIsMainPayment(true);
          setTitlePackage(data.response.title);
          setPaymentId(payment);
        }
      })
      .catch(error => {
        console.error('Logout error:', error);
        alert('Error logging out: ' + error.message);
      })
      .finally(() => {
        setPaymentProcessFinished(true);
      });
    }
  }, [tokens, backend_url, selectedWallet, paymentProcessFinished]);

  const handleTokenSelect = (token) => {
    console.log(token)
    setSelectedTokenAmount(0);
    setRecipientAddress('');
    setShowTransactionDetails(false);
    setShowConfirmation(false);
    setShowTokenDropdown(false);
    setPreTransaction(null);
    setSelectedToken(token);
  };

  const handleBackTransactionDetails = () => {
    setShowTransactionDetails(false);
    setSelectedTokenAmount(0);
  };

  const handleBackSelectedToken = () => {
    setSelectedToken(null);
    setRecipientAddress('');
  }

  const handleAddressSubmit = (e) => {
    e.preventDefault();
    if (recipientAddress) {

      if (selectedWallet.address === recipientAddress) {
        alert("No puedes enviar tokens a la misma wallet.");
        return;
      }

      setShowTransactionDetails(true);
    }
  };

  // const handleLogout = () => {
  //   fetch(`${backend_url}/api/signout`, {
  //     method: 'GET',
  //     credentials: 'include',
  //     headers: {
  //       'Content-Type': 'application/json'
  //     }
  //   })
  //   .then(response => response.json())
  //   .then(data => {
  //     if (data.result) {
  //       setIsLoggedIn(false);
  //       setSelectedToken(null);
  //       setRecipientAddress('');
  //       setShowTransactionDetails(false);
  //       setShowConfirmation(false);
  //       setShowCreateAccount(false);
  //       setNetworks([]);
  //       setSelectedNetwork(null);
  //       setWallets([]);
  //       setSelectedWallet(null);
  //       setTokens([]);
  //       setShowWalletConfig(false);
  //       setPrivateKey('');
  //       setShowPrivateKey(false);
  //       setIsForgetPassword(false);
  //       setShowWalletConfigPrivateKey(false);
  //       setShowTokenDropdown(false);

  //       setIsModalOpen(false);
  //       setModalMessage('');
  //       setModalPrivateKeyMessage('');
  //       setIsModalPrivateKeyOpen(false);
  //       setIsLoading(false);
  //       // setIsMainPayment(false);
  //       setSelectedTokenAmount(0);
  //       setShowPassword(false);
  //       setShowPasswordRegister(false);
  //       setShowPasswordPrivateKey(false);
  //       return data.result;
  //     } else {
  //       alert(data.response || 'Error logging out');
  //     }
  //   })
  //   .catch(error => {
  //     console.error('Logout error:', error);
  //     alert('Error logging out: ' + error.message);
  //   });
  // };

  const handleNextClick = () => {
    const amount = parseFloat(selectedTokenAmount);
    // const balance = parseFloat(selectedToken.balance);

    if (selectedWallet.address === recipientAddress) {
      alert("No puedes enviar tokens a la misma wallet.");
      return;
    }
    
    // if (amount > balance) {
    //   alert(`Insufficient balance. Maximum available: ${selectedToken.balance} ${selectedToken.symbol}`);
    //   return;
    // }

    setIsLoading(true);

    const csrfToken = document.cookie
      .split('; ')
      .find(row => row.startsWith('csrftoken='))
      ?.split('=')[1];

    fetch(`${backend_url}/api/pre_transaction/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken, // Add CSRF token to headers
      },
      credentials: 'include',
      body: JSON.stringify({
        from_address: selectedWallet.address,
        to_address: recipientAddress,
        amount: amount,
        token_id: selectedToken.id,
        network_id: selectedNetwork.id
      }),
    })
    .then(response => response.json())
    .then(data => {
      setPreTransaction(data);
      if (data.result) {
        setShowConfirmation(true);
      }
    })
    .catch(error => {
      console.error('Transaction error:', error);
    })
    .finally(() => {
      setIsLoading(false);
    });
    
  };

  const handleAccountCreation = (e) => {
    e.preventDefault();

    const emailInput = e.target.elements.email;
    const passwordInput = e.target.elements.password;
    const confirmPasswordInput = e.target.elements.confirm_password;

    const fullNameInput = e.target.elements.full_name;
    const countryInput = e.target.elements.country;

    if (!emailInput || !passwordInput || !confirmPasswordInput) {
      console.error('Form inputs not found');
      return;
    }

    const email = emailInput.value;
    const password = passwordInput.value;
    const confirm_password = confirmPasswordInput.value;

    const full_name = fullNameInput.value;
    const country = countryInput.value;

    fetch(`${backend_url}/api/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ email, password, confirm_password, full_name, country }),
    })
    .then(response => response.json())
    .then(data => {
      if (data.result) {

        setModalMessage('Cuenta creada con éxito.');
        setIsModalOpen(true);
        setShowCreateAccount(false);
      } else {
        setModalMessage(data.message);
        setIsModalOpen(true);
      }
    });
  }

  const handleUserProfile = () => {

    fetch(`${backend_url}/api/user`, {
      method: 'GET',
      credentials: 'include',
    })
    .then(response => response.json())
    .then(data => {
      if (data.result) {
        setUserProfile(data.response);
        console.log("data.response ", data.response);
        console.log("userProfile", userProfile);
        setShowWalletConfigUserProfile(true);
      } else {
        setModalMessage(data.message);
        setIsModalOpen(true);
      }
    })
    .catch(error => {
      console.error('Error al obtener el perfil de usuario:', error);
      setIsLoading(false);
    })
    .finally(() => {
      setIsLoading(false);
    });
  }

  const handleNetworkChange = (e) => {
    const network = networks.find(n => n.id === parseInt(e.target.value));
    setSelectedNetwork(network);
    if (network) {
      Cookies.set('selected_network', network.id);
      setTokens([]);
      resetSendFlow();
      fetchWallets(network.id);
    }
  };

  const handleTokenAmountChange = (e) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value) && value >= 0) {
      setPreTransaction(null);
      setSelectedTokenAmount(value);
    }
  };
  

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
    setSelectedTokenAmount(0);
  }
  const handleConfirmTransaction = () => {

    setIsLoading(true);
    
    const csrfToken = document.cookie
      .split('; ')
      .find(row => row.startsWith('csrftoken='))
      ?.split('=')[1];

    fetch(`${backend_url}/api/transaction/make`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken, // Add CSRF token to headers
      },
      credentials: 'include',
      body: JSON.stringify({
        from_address: selectedWallet.address,
        to_address: recipientAddress,
        amount: selectedTokenAmount,
        token_id: selectedToken.id,
        network_id: selectedNetwork.id,
        payment_id: paymentId ? paymentId : null
      }),
    })
    .then(response => response.json())
    .then(data => {
      if (data.result) {
        setShowConfirmation(false);
        setSelectedTokenAmount(0);
        setIsLoading(false);
        setModalMessage('Transacción realizada con éxito.');
        setIsModalOpen(true);
        
        if (paymentId) {
          sendEvent(paymentId);
        }

      } else {
        setIsLoading(false);
        setModalMessage(data.message);
        setIsModalOpen(true);
      }
    })
    .catch(error => {
      console.error('Transaction error:', error);
      if (error.message?.includes('CSRF')) {
        setModalMessage('Security verification failed. Please refresh the page and try again.');
        setIsModalOpen(true);
      }
      setIsLoading(false);
    });
  }

  const ModalPrivateKey = ({ isOpen, onClose, message }) => {
  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const onMaintainPrivateKey = () => {
    setShowPrivateKey(true);
    setIsModalPrivateKeyOpen(false);
  }

  return (
    <div className="ModalOverlay" onClick={handleOverlayClick}>
      <div className="ModalContent">
        <button className="ModalCloseButton" onClick={onClose}>×</button>
        <p className="ModalMessage" style={{fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '0.5rem'}}>{message}</p>
        <p className="ModalDescription">Tu clave privada otorga acceso completo a tu wallet y fondos. No compartas tu clave privada con nadie.</p>
        <button 
          className="RevealKeyButton"
          style={{
            position: 'relative',
            backgroundColor: '#BC944C',
            color: 'white',
            padding: '0.8rem',
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden'
          }}
          onMouseDown={(e) => {
            const startTime = Date.now();
            const duration = 4000; // 4 seconds
            const button = e.currentTarget;
            
            // Create progress indicator
            const progressIndicator = document.createElement('div');
            progressIndicator.style.position = 'absolute';
            progressIndicator.style.top = '0';
            progressIndicator.style.left = '0';
            progressIndicator.style.width = '100%';
            progressIndicator.style.height = '100%';
            progressIndicator.style.borderRadius = '4px';
            progressIndicator.style.border = '2px solid transparent';
            progressIndicator.style.boxSizing = 'border-box';
            progressIndicator.style.pointerEvents = 'none';
            button.appendChild(progressIndicator);
            
            const animateProgress = () => {
              const elapsed = Date.now() - startTime;
              const progress = Math.min(elapsed / duration, 1);
              
              // Calculate the border gradient
              progressIndicator.style.borderImage = 
                `conic-gradient(#1182A3 ${progress * 360}deg, transparent ${progress * 360}deg) 1 / 2px / 0 stretch`;
              
              if (progress < 1) {
                button.dataset.animationFrame = requestAnimationFrame(animateProgress);
              } else {
                onMaintainPrivateKey();
              }
            };
            
            button.dataset.animationFrame = requestAnimationFrame(animateProgress);
            button.dataset.progressIndicator = true;
          }}
          onMouseUp={(e) => {
            const button = e.currentTarget;
            if (button.dataset.animationFrame) {
              cancelAnimationFrame(button.dataset.animationFrame);
              // Remove progress indicator
              const progressIndicator = button.querySelector('div');
              if (progressIndicator) button.removeChild(progressIndicator);
            }
          }}
          onMouseLeave={(e) => {
            const button = e.currentTarget;
            if (button.dataset.animationFrame) {
              cancelAnimationFrame(button.dataset.animationFrame);
              // Remove progress indicator
              const progressIndicator = button.querySelector('div');
              if (progressIndicator) button.removeChild(progressIndicator);
            }
          }}
        >
          <i className="fas fa-lock" style={{marginRight: '8px'}}></i>
          Mantener para revelar clave privada
        </button>
      </div>
    </div>
  );
};

  const handlePrivateKeySubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    const password = e.target.elements.password.value;
    const csrfToken = document.cookie
      .split('; ')
      .find(row => row.startsWith('csrftoken='))
      ?.split('=')[1];
    fetch(`${backend_url}/api/get_wallet_private_key`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken,
      },
      credentials: 'include',
      body: JSON.stringify({
        password: password,
        wallet_address: selectedWallet.address,
      }),
    })
    .then(response => response.json())
    .then(data => {
      if (data.result) {
        setPrivateKey(data.response);
        setModalPrivateKeyMessage("Mantenga para revelar la clave privada");
        setIsModalPrivateKeyOpen(true);
      } else {
        setModalMessage('Error al obtener la clave privada.');
        setIsModalOpen(true);
        setPrivateKey('');
      }
    })
    .catch(error => {
      // console.error('Private key error:', error);
      // setModalMessage('Error al obtener la clave privada.');
      // setIsModalOpen(true);
    })
    .finally(() => {
      setIsLoading(false);
    });
  }

  const handleCloseConfigPrivateKey = () => {
    setShowPrivateKey(false);
    setShowWalletConfigPrivateKey(false);
    setPrivateKey('');
  }

  const handleForgetPassword = () => {
    setIsForgetPassword(true);
  }

  const handleCloseForgetPassword = () => {
    setIsForgetPassword(false);
  }

  const handleForgetPasswordSubmit = (e) => {
    e.preventDefault();
    setIsForgetPassword(false);

    const email = e.target.elements.email.value;

    fetch(`${backend_url}/api/password_recovery/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    })
    .then(response => response.json())
    .then(data => {
      if (data.result) {
        setModalMessage('Correo de recuperación enviado.');
        setIsModalOpen(true);
      } else {
        setModalMessage(data.message);
        setIsModalOpen(true);
      }
    })
    .catch(error => {
      console.error('Error al enviar el correo de recuperación:', error);
      setModalMessage('Error al enviar el correo de recuperación.');
      setIsModalOpen(true);
    });
  }

  const optionsCountryField = {
    countries: [
      { value: 'Argentina', label: 'Argentina' },
      { value: 'Bolivia', label: 'Bolivia' },
      { value: 'Brazil', label: 'Brazil' },
      { value: 'Canada', label: 'Canada' },
      { value: 'Chile', label: 'Chile' },
      { value: 'Colombia', label: 'Colombia' },
      { value: 'Costa Rica', label: 'Costa Rica' },
      { value: 'Cuba', label: 'Cuba' },
      { value: 'Dominican Republic', label: 'Dominican Republic' },
      { value: 'Ecuador', label: 'Ecuador' },
      { value: 'El Salvador', label: 'El Salvador' },
      { value: 'Guatemala', label: 'Guatemala' },
      { value: 'Haiti', label: 'Haiti' },
      { value: 'Honduras', label: 'Honduras' },
      { value: 'Jamaica', label: 'Jamaica' },
      { value: 'Mexico', label: 'Mexico' },
      { value: 'Nicaragua', label: 'Nicaragua' },
      { value: 'Panama', label: 'Panama' },
      { value: 'Paraguay', label: 'Paraguay' },
      { value: 'Peru', label: 'Peru' },
      { value: 'Puerto Rico', label: 'Puerto Rico' },
      { value: 'Uruguay', label: 'Uruguay' },
      { value: 'United States', label: 'United States' },
      { value: 'Venezuela', label: 'Venezuela' }
    ]
  } 

  if (!isLoggedIn) {
    return (
      <div className="WalletWrapper">
        <div className="LoginContainer" style={{background: 'linear-gradient(180deg, rgba(3, 64, 121, 0.72) 0%, rgba(17, 130, 163, 0.72) 19.5%)'}}>
          {!showCreateAccount ? (
            <>
            {!isForgetPassword ? (
              <>
                <div className="LogoContainer">
                  <div className="WalletLogo">
                    <img src="/soluciones-crypto-logo.png" alt="Soluciones Crypto Logo" className="WalletLogoImage" />
                  </div>
                </div>
                <h2 style={{color: 'white'}}>Bienvenido de nuevo</h2>
                <p className="SubTitle" style={{color: 'white'}}>Toma el control de tus criptos. Sé parte del cambio.</p>
                <form className="LoginForm" onSubmit={handleLogin}>
                <input 
                  type="email" 
                  placeholder="Email" 
                  className="LoginInput"
                  name="email"
                />
                <div className="password-input-container" style={{ position: 'relative', width: '100%' }}>
                  <input 
                    type={showPassword ? "text" : "password"}
                    placeholder="Password" 
                    className="LoginInput"
                    name="password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '5px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {showPassword ? <img src="/eye.svg" style={{width: '20px', height: '20px'}} alt="Eye"/> : <img src="/eye-closed.svg" style={{width: '20px', height: '20px'}} alt="Eye closed"/>}
                  </button>
                </div>
                <button type="submit" className="LoginButton" style={{backgroundColor: '#BC944C'}}>
                  Desbloquear
                </button>
              </form>
              <div className="LoginLinks">
                <button className="LinkButton" onClick={() => setShowCreateAccount(true)} style={{color: 'white'}}>
                  Crear cuenta
                </button>
                <button className="LinkButton" onClick={handleForgetPassword} style={{color: 'white'}}>
                  ¿Olvidaste tu contraseña?
                </button>
                </div>
              </>
              ) : (
                <>
                  <div className="ForgetPasswordContainer">
                    <h2 style={{color: 'white'}}>Recuperar contraseña</h2>
                    <p style={{color: 'white'}}>Ingresa tu email para recibir un enlace de restablecimiento de contraseña.</p>
                    <form onSubmit={handleForgetPasswordSubmit}>
                    <input type="email" placeholder="Email" className="LoginInput" name="email" required />
                    <button type="submit" className="LoginButton" style={{backgroundColor: '#BC944C'}}>Recuperar contraseña</button>
                  </form>
                  <button className="LinkButton" onClick={handleCloseForgetPassword} style={{color: 'white'}}>
                    Volver a login
                  </button>
                </div>
                </>
              )}
            </>
          ) : (
            <>
              <div className="LogoContainer">
                {/* <div className="WalletLogo" style={{width: '81px', height: '81px'}}>
                  <img src="/soluciones-crypto-logo.png" alt="Soluciones Crypto Logo" className="WalletLogoImage" />
                </div> */}
              </div>
              <h2 style={{color: 'white'}}>Crear cuenta</h2>
              <p className="SubTitle" style={{color: 'white'}}>Únete a la web descentralizada</p>
              <form className="LoginForm" onSubmit={handleAccountCreation}>
                <input 
                  type="email" 
                  placeholder="Email" 
                  className="LoginInput"
                  name="email"
                  required
                />
                <div className="password-input-container" style={{ position: 'relative', width: '100%' }}>
                  <input 
                    type={showPasswordRegister ? "text" : "password"}
                    placeholder="Contraseña" 
                    className="LoginInput"
                    name="password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswordRegister(!showPasswordRegister)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '5px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {showPasswordRegister ? <img src="/eye.svg" style={{width: '20px', height: '20px'}} alt="Eye"/> : <img src="/eye-closed.svg" style={{width: '20px', height: '20px'}} alt="Eye closed"/>}
                  </button>
                </div>
                <div className="password-input-container" style={{ position: 'relative', width: '100%' }}>
                  <input 
                    type={showPasswordRegister ? "text" : "password"}
                    placeholder="Confirmar contraseña" 
                    className="LoginInput"
                    name="confirm_password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswordRegister(!showPasswordRegister)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '5px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {showPasswordRegister ? <img src="/eye.svg" style={{width: '20px', height: '20px'}} alt="Eye"/> : <img src="/eye-closed.svg" style={{width: '20px', height: '20px'}} alt="Eye closed"/>}
                  </button>
                </div>
                <input 
                  type="text"
                  placeholder="Nombre Completo"
                  className="LoginInput"
                  name="full_name"
                  required
                />
                <select 
                  className="LoginInput"
                  name="country"
                  required
                >
                  <option value="">Seleccione un país</option>
                  {optionsCountryField.countries.map((country) => (
                    <option key={country.value} value={country.value}>
                      {country.label}
                    </option>
                  ))}
                </select>
                <button type="submit" className="LoginButton" style={{backgroundColor: '#BC944C'}}>
                  Crear cuenta
                </button>
              </form>
              <div className="LoginLinks">
                <button className="LinkButton" onClick={() => setShowCreateAccount(false)} style={{color: 'white'}}>
                  Volver a login
                </button>
              </div>
            </>
          )}
        </div>
        <Modal 
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          message={modalMessage}
        />
      </div>
    );
  }

  if (selectedToken && showTransactionDetails) {
    if (showConfirmation) {
      return (
        <div className="WalletWrapper">
          <div className="WalletContainer">
            <div className="SendHeader">
              <button onClick={handleCancelConfirmation} className="BackButton">←</button>
              <h2>Confirmar envío</h2>
            </div>
            <div className="ConfirmationDetails">
              <div className="AddressDetails">
                <div className="AddressRow">
                  <span className="AddressLabel">From:</span>
                  <span className="AddressValue">{selectedWallet.address}</span>
                </div>
                <div className="AddressRow">
                  <span className="AddressLabel">To:</span>
                  <span className="AddressValue">{recipientAddress}</span>
                </div>
              </div>

              <div className="WarningMessage">
                <span className="WarningIcon">⚠️</span>
                <p>You are sending {selectedTokenAmount} {selectedToken.symbol}</p>
              </div>
              
              <div className="EstimatedCharges">
                <h3>Estimated charges</h3>
                <p>No charges predicted for your wallet</p>
              </div>
              
              <div className="FeeDetails">
                <div className="FeeRow">
                  <span>Balance: {preTransaction && preTransaction.trx_balance} {selectedNetwork.symbol}</span>
                </div>
                <div className="FeeSubtext">
                  Tarifa estimada:  {preTransaction && preTransaction.fee_trx}  {selectedNetwork.symbol}
                </div>
                {!preTransaction.result &&
                  <div className="FeeSubtext">
                    <i className="fas fa-exclamation-triangle"></i> {preTransaction && preTransaction.response}
                  </div>
                }
              </div>
              
              <div className="TransactionActions">
                <button className="CancelButton" onClick={handleCancelConfirmation}>
                  Cancelar
                </button>
                <button className="ConfirmButton" onClick={handleConfirmTransaction}>
                  Confirmar
                </button>
              </div>
            </div>
          </div>
          {isLoading && <Loader />}
        </div>
      );
    }
    
    return (
      <div className="WalletWrapper">
        <div className="WalletContainer">
          <div className="SendHeader">
            {isMainPayment ?
                <h2>{titlePackage}</h2>
              :
                <>
                  <button onClick={handleBackTransactionDetails} className="BackButton">←</button>
                  <h2>Enviar</h2>
                </>
              }
          </div>
          <div className="TransactionDetails">
            <div className="AddressDisplay">
              <label>ENVIAR</label>
              <div className="AddressBox">
                <span className="Address">{recipientAddress}</span>
                {/* <button className="ClearAddress">×</button> */}
              </div>
            </div>
            
            <div className="TokenSelector">
              <div className="TokenSelected">
                <div className="TokenIcon">
                  <img 
                    src={selectedToken.symbol === selectedNetwork.symbol ? '/token-trx-icon.png' : '/token-usdt-icon.png'} 
                    alt={selectedToken.symbol} 
                    style={{width: '20px', height: '20px'}}
                  />
                </div>
                <div className="TokenInfo">
                  <div className="TokenInfoLabel"></div>
                  <label style={{fontSize: '15px', color: '#666', fontWeight: '500'}}>Saldo disponible:</label>
                  <div className="AmountMax">{selectedToken.balance} {selectedToken.symbol}</div>
                </div>
                {!isMainPayment && 
                  <div className="ChangeToken" onClick={() => setShowTokenDropdown(!showTokenDropdown)}>
                    <span className="DropdownArrow">▼</span>
                  </div>
                }
              </div>
              {showTokenDropdown && (
                <div className="TokenDropdown">
                  {tokens.map((token) => (
                    <div 
                      key={`${selectedWallet?.id || 'no_wallet'}_${token.id || token.symbol}`} 
                      className="TokenDropdownItem"
                      onClick={() => {
                        setSelectedToken(token);
                        setShowTokenDropdown(false);
                      }}
                    >
                      <div className="TokenIcon">
                        <img 
                          src={token.symbol === selectedNetwork.symbol ? '/token-trx-icon.png' : '/token-usdt-icon.png'} 
                          alt={token.symbol} 
                          style={{width: '20px', height: '20px'}}
                        />
                      </div>
                      <span>{token.symbol}</span>
                      <span className="TokenBalance">{token.balance}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="AmountInput">
                <div className="AmountInputLabel">
                  <label>Cantidad a enviar:</label>
                </div>
                <div className="AmountInputField">
                  <input 
                    type="text" 
                    placeholder="0" 
                    value={selectedTokenAmount}
                    onChange={handleTokenAmountChange}
                    readOnly={isMainPayment}
                  />
                  <span>{selectedToken.symbol}</span>
                </div>
              </div>
            </div>
            <label style={{color:"red"}}>Para comprar la tarjeta o realizar cualquier transacción necesitas TRX</label>
            {preTransaction && !preTransaction.result &&
            <>
              <div className="FeeEstimate">
                  <div className="FeeLabel">
                    <span>Balance: {preTransaction && preTransaction.trx_balance} {selectedNetwork.symbol}</span>
                  </div>
                  <div className="FeeSubtext">
                    Tarifa estimada:  {preTransaction && preTransaction.fee_trx}  {selectedNetwork.symbol}
                  </div>
              </div>
              <div className="FeeEstimate">
                  <div className="FeeSubtext">
                   ⚠️ {preTransaction && preTransaction.response}
                  </div>
              </div>
            </>
            }

            <div className="TransactionActions">
              {!isMainPayment &&
                <button className="CancelButton" onClick={handleBackTransactionDetails}>Cancelar</button>
              }
              <button className="NextButton" onClick={handleNextClick}>Siguiente</button>
            </div>
          </div>
        </div>
        <Modal 
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          message={modalMessage}
        />
        {isLoading && <Loader />}
      </div>
    );
  }

  if (selectedToken) {
    return (
      <div className="WalletWrapper">
        <div className="WalletContainer">
          <div className="SendHeader">
            <button onClick={handleBackSelectedToken} className="BackButton">←</button>
            <h2>Enviar {selectedToken.symbol}</h2>
          </div>
          <form className="SendForm" onSubmit={handleAddressSubmit}>
            <div className="SendInput">
              <label>Enviar a</label>
              <input 
                type="text" 
                placeholder="Dirección del destinatario" 
                value={recipientAddress}
                onChange={(e) => setRecipientAddress(e.target.value)}
                readOnly={isMainPayment}
              />
            </div>
            <div className="TokenBalance">
              <span>Balance: {selectedToken.balance} {selectedToken.symbol}</span>
            </div>
            <button type="submit" className="NextButton" style={{width: '100%'}}>Siguiente</button>
            <button onClick={handleBackSelectedToken} className="CancelButton" style={{width: '100%', marginTop: '10px'}}>Cancelar</button>
            <div className="NetworkIndicator">
              <span className="NetworkLabel">Red:</span>
              <span className="NetworkName">{selectedNetwork.name}</span>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (showWalletConfig && selectedWallet) {

  if (showWalletConfigPrivateKey) {
    return (
        <div className="WalletWrapper">
          <div className="WalletContainer">
            <div className="ConfigHeader">
              <button onClick={handleCloseConfigPrivateKey} className="BackButton">←</button>
              <h2>Clave privada</h2>
            </div>
            <div className="ConfigContent">
              <div className="ConfigSectionPrivateKey">
                <div className="WalletDetail">
                  <span className="DetailLabel">Nombre:</span>
                  <span className="DetailValue">{selectedWallet.name}</span>
                </div>
                <div className="WalletDetail">
                  <span className="DetailLabel">Dirección:</span>
                  <span className="DetailValue">{selectedWallet.address}</span>
                </div>
                <div className="WalletDetail">
                  <span className="DetailLabel">Red:</span>
                  <span className="DetailValue">{selectedNetwork.name}</span>
                </div>
              </div>
              
              <div className="ConfigSection">
                <h3>Revelar clave privada</h3>
                <p className="SecurityWarning">⚠️ No compartas tu clave privada con nadie!</p>
                
                {showPrivateKey?
                <div className="PrivateKeyContainer">
                  <div className="PrivateKeyDisplay">
                    <div className="PrivateKeyActions">
                      <button className="CopyKeyButton" onClick={() => {
                        if (privateKey) {
                          navigator.clipboard.writeText(privateKey)
                            .then(() => {
                              setModalMessage('Clave privada copiada al portapapeles!');
                              setIsModalOpen(true);
                            })
                            .catch(err => {
                              console.error('Failed to copy private key:', err);
                              setModalMessage('Por algun motivo no se pudo copiar la clave privada.');
                              setIsModalOpen(true);
                            });
                        }
                      }}>
                        <i className="fas fa-copy"></i> Copiar clave privada
                      </button>
                      <button className="HideKeyButton" onClick={() => setShowPrivateKey(false)}>
                        <i className="fas fa-eye-slash"></i> Ocultar
                      </button>
                    </div>
                    <span className="PrivateKey">{privateKey}</span>
                  </div>
                </div>
                :
                <form onSubmit={handlePrivateKeySubmit}>
                  <span className="DetailLabel">Contraseña necesaria para revelar clave privada:</span>
                  <div className="PasswordInputContainer" style={{position: 'relative', width: '100%'}}>
                    <input type={showPasswordPrivateKey ? "text" : "password"} placeholder="Contraseña" className="LoginInput" name="password" required />
                    <button type="button" onClick={() => setShowPasswordPrivateKey(!showPasswordPrivateKey)} style={{position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                      {showPasswordPrivateKey ? <img src="/eye.svg" style={{width: '20px', height: '20px'}} alt="Eye"/> : <img src="/eye-closed.svg" style={{width: '20px', height: '20px'}} alt="Eye closed"/>}
                    </button>
                  </div>
                  <button type="submit" className="LoginButton" style={{backgroundColor: '#BC944C'}}>Revelar clave privada</button>
                </form>
                }
              </div>
            </div>
          </div>
          <ModalPrivateKey 
            isOpen={isModalPrivateKeyOpen}
            onClose={() => setIsModalPrivateKeyOpen(false)}
            message={modalPrivateKeyMessage}
          />
          <Modal 
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            message={modalMessage}
          />
          {isLoading && <Loader />}
        </div>
      );
    }

    if (showWalletConfigUserProfile) {
      return (
        <div className="WalletWrapper">
          <div className="WalletContainer">
            <div className="ConfigHeader">
              <button onClick={() => setShowWalletConfigUserProfile(false)} className="BackButton">←</button>
              <h2>Perfil de usuario</h2>
            </div>
            <div className="ConfigOptions">
              <div className="ConfigSection">
                {userProfile && (
                  <div className="ProfileDetails">
                    <div className="ProfileDetailRow">
                      <span className="DetailLabel">Nombre completo:</span>
                      <span className="DetailValue">{userProfile.full_name}</span>
                    </div>
                    <div className="ProfileDetailRow">
                      <span className="DetailLabel">País:</span>
                      <span className="DetailValue">{userProfile.country}</span>
                    </div>
                    {/* <div className="ProfileDetailRow">
                      <span className="DetailLabel">Estado:</span>
                      <span className="DetailValue">{userProfile.is_active ? 'Activo' : 'Inactivo'}</span>
                    </div> */}
                    <div className="ProfileDetailRow">
                      <span className="DetailLabel">Fecha de creación:</span>
                      <span className="DetailValue">{new Date(userProfile.created_at).toLocaleDateString()}</span>
                    </div>
                    {/* <div className="ProfileDetailRow">
                      <span className="DetailLabel">Última actualización:</span>
                      <span className="DetailValue">{new Date(userProfile.updated_at).toLocaleDateString()}</span>
                    </div> */}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="WalletWrapper">
        <div className="WalletContainer">
          <div className="ConfigHeader">
            <button onClick={() => setShowWalletConfig(false)} className="BackButton">←</button>
            <h2>Menú de configuración</h2>
          </div>
          <div className="ConfigOptions">
            <button className="ConfigSectionButton" onClick={() => setShowWalletConfigPrivateKey(true)}>
              <span className="DetailLabelConfig">Configuración de clave privada</span>
            </button>
            <button className="ConfigSectionButton" onClick={handleUserProfile}>
              <span className="DetailLabelConfig">Perfil de usuario</span>
            </button>
          </div>
          <div className="ConfigOptions" style={{height: '300px'}}>

          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="WalletWrapper">
      <div className="WalletContainer">
        <div className="WalletHeader">
          <div className="NetworkSelectorContainer">
            <div className="SelectorWrapper">
              <div className="NetworkSelector" onClick={(e) => {
                // Find the select element within this div and trigger a click on it
                const selectElement = e.currentTarget.querySelector('.NetworkSelect');
                if (selectElement) {
                  selectElement.click();
                }
              }}>
                <div className="NetworkIcon">
                  <img 
                    src={'/token-trx-icon.png'} 
                    alt={selectedNetwork.symbol} 
                    style={{width: '20px', height: '20px'}}
                  />
                </div>
                <select 
                  value={selectedNetwork?.id || ''}
                  onChange={handleNetworkChange}
                  className="NetworkSelect"
                  disabled={isMainPayment}
                >
                  {networks.map((network) => (
                    <option key={network.id} value={network.id} className="NetworkOption" style={{width: '100%'}}>
                      {network.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="WalletSelector">
                <div className="WalletIcon">👛</div>
                <select
                  value={selectedWallet?.id || ''}
                  onChange={(e) => {
                    const wallet = wallets.find(w => w.id === parseInt(e.target.value));
                    if (!wallet) return;
                    if (wallet.id === selectedWallet?.id) return;
                    setTokens([]);
                    resetSendFlow();
                    setSelectedWallet(wallet);
                    tokens_fetch(wallet.address).then(nextTokens => {
                      if (nextTokens && selectedWalletRef.current?.address === wallet.address) {
                        setTokens(nextTokens);
                      }
                    });
                  }}
                  className="WalletSelect"
                  disabled={!selectedNetwork || isMainPayment}
                >
                  {wallets.map((wallet) => (
                    <option key={wallet.id} value={wallet.id} className="WalletOption" style={{width: '100%'}}>
                      {wallet.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="ConfigButton" onClick={() => setShowWalletConfig(true)}>
                <span className="ConfigIcon">☰</span>
              </div>
            </div>
          </div>
          <div className="AccountInfo">
            <span className="AccountBalance">{tokens.length > 0 ? tokens[0].balance : '0'}</span>
            <span className="CurrencyLabel">{tokens.length > 0 ? tokens[0].symbol : ''}</span>
          </div>
          <span style={{fontSize: '15px', color: '#BC944C'}}>Esta dirección de tu wallet la puedes usar para recibir TRX y también USDT:</span>
          <div className="WalletAddressCopy" onClick={() => {
            if (selectedWallet?.address) {
              navigator.clipboard.writeText(selectedWallet.address)
                .then(() => {
                  setModalMessage('Dirección copiada al portapapeles!');
                  setIsModalOpen(true);
                })
                .catch(err => {
                  console.error('Failed to copy address:', err);
                  setModalMessage('Por algun motivo no se pudo copiar la dirección.');
                  setIsModalOpen(true);
                });
            }
          }}>
            <span className="AddressText">{selectedWallet?.address || 'No wallet selected'}</span>
            <span className="CopyIcon">📋</span>
          </div>
        </div>
        
        <div className="TokenList">
          {tokens.map((token) => (
            <div 
              key={`${selectedWallet?.id || 'no_wallet'}_${token.id || token.symbol}`} 
              className="TokenItem"
              onClick={() => handleTokenSelect(token)}
            >
              <div className="TokenIcon">
                <img 
                  src={token.symbol === selectedNetwork.symbol ? '/token-trx-icon.png' : '/token-usdt-icon.png'} 
                  alt={token.symbol} 
                  style={{width: '20px', height: '20px'}}
                />
              </div>
              <div className="TokenInfo">
                <span className="TokenName">{token.symbol}</span>
                <span className="TokenBalance">{token.symbol}</span>
              </div>
              <div className="TokenValue">
                <span className="TokenAmount">{token.balance} {token.symbol}</span>
                {/* <span className="TokenUSDValue">{token.balance} USD</span> */}
              </div>
              <div className="SendButtonContainer" onClick={() => handleTokenSelect(token)}>
                <button className="SendButton">Enviar</button>
              </div>
            </div>
          ))}
        </div>
        {/* <div className="LogoutButtonContainer" onClick={handleLogout} style={{display: hiddenLogout ? 'none' : 'block'}}>
          <button className="LogoutButton">Cerrar sesión</button>
        </div> */}
      </div>
      <Modal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        message={modalMessage}
      />
      {isLoading && <Loader />}
    </div>
  );
}

export default App;
